import { ProjectLayout } from '../../layouts/project-layout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { router } from '@inertiajs/react'
import { Pencil, Check, X, ChevronDown, MoreVertical, Trash2, Paperclip, Maximize2, Code2, Globe, Send, ChevronUp, MessageSquare } from 'lucide-react'
import { FilePreview } from '../../components/conversations/FilePreview'
import { useFileUploadWithDirectUpload } from '../../hooks/useFileUploadWithDirectUpload'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Input } from '../../components/ui/input'
import { Textarea } from '../../components/ui/textarea'
import { useState } from 'react'
import { toast } from 'sonner'

interface Project {
  id: number
  name: string
  description: string | null
  slug: string
  vps_status: string | null
  is_archived: boolean
  created_at: string
  updated_at: string
  can_generate: boolean
  can_deploy: boolean
  sandbox_url: string | null
  live_url: string | null
}

interface Conversation {
  id: number
  title: string
  status: string
  mode: string
  message_count: number
  created_at: string
  updated_at: string
  url: string
}

interface Pagination {
  page: number
  pages: number
  count: number
  from: number
  to: number
}

interface ProjectShowProps {
  project: Project
  ongoing_conversation: Conversation | null
  conversations: Conversation[]
  pagination: Pagination
  has_ongoing_conversation: boolean
  ongoing_conversation_id?: number
  current_status_filter: string
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function Show({ project, ongoing_conversation, conversations, pagination, has_ongoing_conversation, current_status_filter }: ProjectShowProps) {
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false)
  const [conversationToDiscard, setConversationToDiscard] = useState<number | null>(null)
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState(project.name)
  const [featureDescription, setFeatureDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCreateFeatureExpanded, setIsCreateFeatureExpanded] = useState(false)
  const [isSandboxModalOpen, setIsSandboxModalOpen] = useState(false)
  const [isChatVisible, setIsChatVisible] = useState(true)
  const [chatMessage, setChatMessage] = useState('')
  const [isSendingMessage, setIsSendingMessage] = useState(false)

  const {
    selectedFiles,
    uploadedFiles,
    uploadProgress,
    allFilesUploaded,
    hasFailedUploads,
    isUploading,
    isDragging,
    fileInputRef,
    handleFileSelect,
    removeFile,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    clearFiles,
    retryUpload,
    getFileKey
  } = useFileUploadWithDirectUpload()

  const handleDiscardFeature = (conversationId: number) => {
    setConversationToDiscard(conversationId)
    setIsDiscardDialogOpen(true)
  }

  const confirmDiscardFeature = async () => {
    if (!conversationToDiscard) return

    try {
      const response = await fetch(`/projects/${project.id}/features/${conversationToDiscard}/discard_changes`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      })

      if (response.ok) {
        // Close dialog and redirect to feature page to show discard in progress
        setIsDiscardDialogOpen(false)
        const conversationId = conversationToDiscard
        setConversationToDiscard(null)
        router.visit(`/projects/${project.id}/features/${conversationId}`)
      } else {
        console.error('Failed to discard feature')
        // Optionally show error toast
      }
    } catch (error) {
      console.error('Error discarding feature:', error)
    }
  }

  const handleStatusFilter = (status: string) => {
    router.get(`/projects/${project.id}`, { status }, { preserveScroll: true, preserveState: true })
  }

  const handlePageChange = (page: number) => {
    router.get(`/projects/${project.id}`, { page, status: current_status_filter }, { preserveScroll: true, preserveState: true })
  }

  const handleNameSave = () => {
    if (editedName.trim() && editedName !== project.name) {
      router.patch(`/projects/${project.id}`, {
        project: { name: editedName.trim() }
      }, {
        preserveScroll: true,
        onSuccess: () => setIsEditingName(false)
      })
    } else {
      setIsEditingName(false)
      setEditedName(project.name)
    }
  }

  const handleNameCancel = () => {
    setIsEditingName(false)
    setEditedName(project.name)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNameSave()
    } else if (e.key === 'Escape') {
      handleNameCancel()
    }
  }

  const handleFeatureSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!featureDescription.trim() || isSubmitting) return

    // Check if uploads are in progress or failed
    if (selectedFiles.length > 0 && (!allFilesUploaded || hasFailedUploads)) {
      if (hasFailedUploads) {
        toast.error('Some files failed to upload. Please retry or remove them.')
      } else {
        toast.error('Please wait for files to finish uploading')
      }
      return
    }

    setIsSubmitting(true)

    try {
      // Use FormData with signed_blob_ids
      const formData = new FormData()
      formData.append('conversation[description]', featureDescription.trim())

      if (uploadedFiles.length > 0) {
        uploadedFiles.forEach(uploadedFile => {
          formData.append('conversation[signed_blob_ids][]', uploadedFile.signedBlobId)
        })
      }

      const response = await fetch(`/projects/${project.id}/features`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: formData,
        redirect: 'follow' // Follow redirects to get the final URL
      })

      if (response.ok) {
        // Clear form
        setFeatureDescription('')
        clearFiles()

        // Navigate to the new conversation page using the final URL after redirect
        router.visit(response.url)
      } else {
        console.error('Failed to create feature')
      }
    } catch (error) {
      console.error('Error creating feature:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFeatureKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!isSubmitting && featureDescription.trim()) {
        handleFeatureSubmit(e)
      }
    }
  }

  const handleSandboxChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!chatMessage.trim() || isSendingMessage) return

    setIsSendingMessage(true)

    try {
      // If there's an ongoing conversation, send message to it and redirect
      if (ongoing_conversation) {
        const response = await fetch(`/projects/${project.id}/features/${ongoing_conversation.id}/messages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          body: JSON.stringify({
            message: {
              content: chatMessage.trim()
            }
          })
        })

        if (response.ok) {
          // Close modal and redirect to conversation page
          setIsSandboxModalOpen(false)
          setChatMessage('')
          router.visit(ongoing_conversation.url)
        } else {
          const data = await response.json()
          console.error('Failed to send message:', data)
          setIsSendingMessage(false)
        }
      } else {
        // Create new conversation using FormData with fetch and explicit CSRF token
        const formData = new FormData()
        formData.append('conversation[description]', chatMessage.trim())

        const response = await fetch(`/projects/${project.id}/features`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          body: formData,
          redirect: 'follow' // Follow redirects to get the final URL
        })

        if (response.ok) {
          // Close modal and clear message
          setIsSandboxModalOpen(false)
          setChatMessage('')

          // The response.url will be the final URL after redirect
          // Extract the conversation ID from the URL and navigate there
          router.visit(response.url)
        } else {
          console.error('Failed to create conversation')
          setIsSendingMessage(false)
        }
      }
    } catch (error) {
      console.error('Error sending message:', error)
      setIsSendingMessage(false)
    }
  }

  const handleChatKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without Shift), Shift+Enter for new line
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!isSendingMessage && chatMessage.trim()) {
        handleSandboxChatSubmit(e)
      }
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getStatusBadgeVariant = (status: string | null): "default" | "secondary" | "destructive" | "outline" => {
    if (!status) return 'secondary'

    switch (status) {
      case 'running':
        return 'default'
      case 'vps_provisioning':
        return 'secondary'
      case 'error':
        return 'destructive'
      case 'stopped':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  const formatStatus = (status: string | null) => {
    if (!status) return null
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  }

  const getConversationStatusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" | "warning" => {
    switch (status) {
      case 'in_progress':
        return 'warning'
      case 'accepted':
        return 'default'
      case 'discarded':
        return 'outline'
      default:
        return 'outline'
    }
  }

  const formatConversationStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  }

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < 60) return 'just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`
    return formatDate(dateString)
  }

  return (
    <>
      {/* Discard Feature Confirmation Dialog */}
      <AlertDialog open={isDiscardDialogOpen} onOpenChange={setIsDiscardDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Feature?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to discard this feature? All changes will be lost and this action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setIsDiscardDialogOpen(false)
              setConversationToDiscard(null)
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDiscardFeature}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard Feature
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sandbox Interactive Modal */}
      <Dialog open={isSandboxModalOpen} onOpenChange={(open) => {
        setIsSandboxModalOpen(open)
        // Reset chat message when closing modal
        if (!open) {
          setChatMessage('')
          setIsChatVisible(true)
        }
      }}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] h-[95vh] p-0 flex flex-col">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Code2 className="h-5 w-5 text-green-600" />
              Sandbox Environment
            </DialogTitle>
            <DialogDescription>
              Interactive preview of your development environment
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Iframe Container */}
            <div className="flex-1 overflow-hidden">
              <iframe
                src={project.sandbox_url || ''}
                className="w-full h-full border-0"
                title="Sandbox Environment"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads allow-popups-to-escape-sandbox allow-storage-access-by-user-activation"
                allow="microphone; camera; geolocation; clipboard-read; clipboard-write; fullscreen; autoplay; encrypted-media; payment; web-share"
              />
            </div>

            {/* Chat Section */}
            {isChatVisible ? (
              <div className="border-t bg-background">
                <div className="px-6 py-3 border-b flex items-center justify-between bg-muted/50">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-medium">Chat with AI</h3>
                    {ongoing_conversation && (
                      <Badge variant="warning" className="text-xs">
                        Feature in progress
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsChatVisible(false)}
                    className="h-7"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </div>

                <div className="p-4">
                  <form onSubmit={handleSandboxChatSubmit} className="flex gap-2 items-end">
                    <Textarea
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      onKeyDown={handleChatKeyDown}
                      placeholder={
                        ongoing_conversation
                          ? "Continue the conversation..."
                          : "Describe what you want to add or change..."
                      }
                      disabled={isSendingMessage}
                      className="flex-1 min-h-[80px] resize-none"
                      rows={3}
                    />
                    <Button
                      type="submit"
                      size="icon"
                      disabled={isSendingMessage || !chatMessage.trim()}
                      className="mb-0.5"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </form>

                  {!ongoing_conversation && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Start a new feature conversation by describing your request
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsChatVisible(true)}
                  className="w-full py-2 flex items-center justify-center gap-2"
                >
                  <ChevronUp className="h-4 w-4" />
                  <span className="text-sm">Show Chat</span>
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="mx-auto w-full max-w-7xl flex-col gap-4 py-4 px-4 sm:px-6 md:gap-6 md:py-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                {isEditingName ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className="text-3xl font-bold h-auto py-1 px-2"
                      autoFocus
                    />
                    <button
                      onClick={handleNameSave}
                      className="p-1.5 hover:bg-accent rounded-md transition-colors"
                      title="Save"
                    >
                      <Check className="w-5 h-5 text-green-600" />
                    </button>
                    <button
                      onClick={handleNameCancel}
                      className="p-1.5 hover:bg-accent rounded-md transition-colors"
                      title="Cancel"
                    >
                      <X className="w-5 h-5 text-destructive" />
                    </button>
                  </div>
                ) : (
                  <div className="group flex items-center gap-2">
                    <h2 className="text-3xl font-bold text-foreground">{project.name}</h2>
                    {!project.is_archived && (
                      <button
                        onClick={() => setIsEditingName(true)}
                        className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-accent rounded-md transition-all"
                        title="Rename project"
                      >
                        <Pencil className="w-4 h-4 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                )}
                {project.is_archived && (
                  <Badge variant="warning">
                    Archived
                  </Badge>
                )}
              </div>
              {project.description && (
                <p className="text-muted-foreground mt-1">{project.description}</p>
              )}
            </div>
            {formatStatus(project.vps_status) && (
              <Badge variant={getStatusBadgeVariant(project.vps_status)} className="text-sm">
                {formatStatus(project.vps_status)}
              </Badge>
            )}
          </div>
        </div>

        {/* Create Feature */}
        {!project.is_archived && (
          <Card className="mb-6 max-w-3xl mx-auto">
            <CardHeader
              className="cursor-pointer"
              onClick={() => setIsCreateFeatureExpanded(!isCreateFeatureExpanded)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Create New Feature</CardTitle>
                  <CardDescription>
                    Describe what feature you want to add to your project
                  </CardDescription>
                </div>
                <ChevronDown
                  className={`w-5 h-5 transition-transform duration-200 ${isCreateFeatureExpanded ? 'rotate-180' : ''}`}
                />
              </div>
            </CardHeader>

            {isCreateFeatureExpanded && (
              <CardContent>
                {has_ongoing_conversation ? (
                  <div className="p-4 text-sm text-destructive bg-destructive/10 rounded-lg border border-destructive/20">
                    You already have a feature in progress. Please complete or discard it before starting a new one.
                  </div>
                ) : (
                  <form onSubmit={handleFeatureSubmit} className="space-y-4">
                    <div
                      className={`relative transition-all ${
                        isDragging
                          ? 'border-2 border-dashed border-primary bg-primary/5 rounded-xl p-2'
                          : ''
                      }`}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                    >
                      {/* Drag overlay */}
                      {isDragging && (
                        <div className="absolute inset-0 bg-primary/10 rounded-xl flex items-center justify-center z-10 pointer-events-none animate-in fade-in duration-200">
                          <div className="text-center space-y-1">
                            <div className="inline-block p-2 bg-primary/10 rounded-full">
                              <Paperclip className="h-6 w-6 text-primary" />
                            </div>
                            <p className="text-xs font-medium text-primary">Drop files here</p>
                            <p className="text-[11px] text-muted-foreground">Images, documents, and data files</p>
                          </div>
                        </div>
                      )}

                      {/* File Preview */}
                      {selectedFiles.length > 0 && (
                        <div className="mb-3">
                          <FilePreview
                            files={selectedFiles}
                            uploadProgress={uploadProgress}
                            onRemove={removeFile}
                            onRetry={retryUpload}
                            getFileKey={getFileKey}
                          />
                        </div>
                      )}

                      <div className="relative">
                        <Textarea
                          value={featureDescription}
                          onChange={(e) => setFeatureDescription(e.target.value)}
                          onKeyDown={handleFeatureKeyDown}
                          placeholder="I want to add a user authentication system with login and signup..."
                          disabled={isSubmitting}
                          className="min-h-[120px] resize-none text-base pb-14"
                          autoFocus
                        />

                        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 px-1.5 py-1 text-muted-foreground hover:text-foreground hover:bg-muted"
                              onClick={() => fileInputRef.current?.click()}
                              title="Attach files"
                            >
                              <Paperclip className="h-3.5 w-3.5" />
                            </Button>

                          </div>

                          <Button
                            type="submit"
                            size="sm"
                            disabled={isSubmitting || !featureDescription.trim() || isUploading || (selectedFiles.length > 0 && !allFilesUploaded)}
                            title={isUploading ? 'Files are uploading...' : undefined}
                          >
                            {isSubmitting ? 'Creating...' : (isUploading ? 'Uploading...' : 'Create Feature')}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.docx,.txt,.md,.csv,.json,.xlsx"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </form>
                )}
              </CardContent>
            )}
          </Card>
        )}

        {/* Environment URLs */}
        {project.sandbox_url && (
          <div className="mb-4 flex justify-center px-4">
            <div className="flex gap-4 justify-center max-w-full">
              {/* Sandbox URL Card */}
              <div className="bg-green-600/40 hover:bg-green-600 group relative overflow-hidden rounded-xl p-px transition-all duration-300 ease-in-out flex-shrink min-w-0 w-full" style={{ maxWidth: '564px' }}>
                <Card className="h-full border-0 shadow-none bg-card w-full">
                  <CardContent className="px-8 py-6">
                    <div className="relative border rounded-lg overflow-hidden bg-muted w-full aspect-[3/2] mb-4" style={{ maxWidth: '500px' }}>
                      <iframe
                        src={project.sandbox_url}
                        className="w-full h-full border-0 pointer-events-none"
                        title="Sandbox Environment"
                        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads allow-popups-to-escape-sandbox allow-storage-access-by-user-activation"
                        allow="microphone; camera; geolocation; clipboard-read; clipboard-write; fullscreen; autoplay; encrypted-media; payment; web-share"
                      />
                      <Button
                        variant="secondary"
                        size="icon"
                        className="absolute top-1 right-1 h-5 w-5 rounded shadow-sm pointer-events-auto z-10"
                        onClick={() => setIsSandboxModalOpen(true)}
                        title="Open interactive preview"
                      >
                        <Maximize2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <h6 className="mb-2 text-lg font-semibold flex items-center gap-2">
                      <Code2 className="h-5 w-5 text-green-600" />
                      Sandbox
                    </h6>
                    <a
                      href={project.sandbox_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-900 hover:underline block truncate"
                      title={project.sandbox_url}
                    >
                      {project.sandbox_url}
                    </a>
                  </CardContent>
                </Card>
              </div>

              {/* Live URL Card */}
              <div className="bg-red-600/40 hover:bg-red-600 group relative overflow-hidden rounded-xl p-px transition-all duration-300 ease-in-out flex-shrink min-w-0 w-full" style={{ maxWidth: '564px' }}>
                <Card className="h-full border-0 shadow-none bg-card w-full">
                  <CardContent className="px-8 py-6">
                    {project.live_url ? (
                      <>
                        <div className="relative border rounded-lg overflow-hidden bg-muted w-full aspect-[3/2] mb-4" style={{ maxWidth: '500px' }}>
                          <iframe
                            src={project.live_url}
                            className="w-full h-full border-0 pointer-events-none"
                            title="Live Environment"
                            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads allow-popups-to-escape-sandbox"
                            allow="microphone; camera; geolocation; clipboard-read; clipboard-write; fullscreen; autoplay; encrypted-media; payment; web-share"
                          />
                          <Button
                            variant="secondary"
                            size="icon"
                            className="absolute top-1 right-1 h-5 w-5 rounded shadow-sm pointer-events-auto z-10"
                            onClick={() => window.open(project.live_url!, '_blank')}
                            title="Open in new tab"
                          >
                            <Maximize2 className="h-3 w-3" />
                          </Button>
                        </div>
                        <h6 className="mb-2 text-lg font-semibold flex items-center gap-2">
                          <Globe className="h-5 w-5 text-red-600" />
                          Live
                        </h6>
                        <a
                          href={project.live_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-900 hover:underline block truncate"
                          title={project.live_url}
                        >
                          {project.live_url}
                        </a>
                      </>
                    ) : (
                      <>
                        <div className="border rounded-lg overflow-hidden bg-muted flex items-center justify-center w-full aspect-[3/2] mb-4" style={{ maxWidth: '500px' }}>
                          <p className="text-muted-foreground text-xs">No preview</p>
                        </div>
                        <h6 className="mb-2 text-lg font-semibold flex items-center gap-2">
                          <Globe className="h-5 w-5 text-red-600" />
                          Live
                        </h6>
                        <p className="text-xs text-muted-foreground">Not deployed yet</p>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* Features Section */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-4 items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-foreground">Features</h3>
            <Select value={current_status_filter} onValueChange={handleStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="discarded">Discarded</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Features Table */}
          {!ongoing_conversation && conversations.length === 0 ? (
            <Card className="shadow-none">
              <CardContent className="pt-6">
                <div className="text-center py-12">
                  <div className="text-muted-foreground mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-foreground mb-2">No features yet</h4>
                  <p className="text-muted-foreground">
                    {project.is_archived
                      ? 'This project is archived. Unarchive it to start building features.'
                      : 'Use the form above to create your first feature'}
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
                <table className="w-full">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Messages
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Last Updated
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {/* In-Progress Conversation - Always First */}
                    {ongoing_conversation && (
                      <tr
                        className="border-l-4 border-l-warning bg-warning/5 hover:bg-warning/10 transition-colors cursor-pointer"
                        onClick={(e) => {
                          // Don't navigate if clicking on the actions column
                          if (!(e.target as HTMLElement).closest('.actions-column')) {
                            router.visit(ongoing_conversation.url)
                          }
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-foreground">
                            {ongoing_conversation.title}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant="warning">
                            {formatConversationStatus(ongoing_conversation.status)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm text-foreground">
                            {ongoing_conversation.message_count}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-foreground">
                            {formatTimeAgo(ongoing_conversation.updated_at)}
                          </span>
                        </td>
                        <td
                          className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium actions-column"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {!project.is_archived && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDiscardFeature(ongoing_conversation.id)
                                  }}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Discard feature
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      </tr>
                    )}

                    {/* Other Conversations */}
                    {conversations.map((conversation) => (
                      <tr
                        key={conversation.id}
                        className="hover:bg-muted transition-colors cursor-pointer"
                        onClick={(e) => {
                          // Don't navigate if clicking on the actions column
                          if (!(e.target as HTMLElement).closest('.actions-column')) {
                            router.visit(conversation.url)
                          }
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-foreground">
                            {conversation.title}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant={getConversationStatusBadgeVariant(conversation.status)}>
                            {formatConversationStatus(conversation.status)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm text-foreground">
                            {conversation.message_count}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-foreground">
                            {formatTimeAgo(conversation.updated_at)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium actions-column">
                          {/* No actions for completed conversations */}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="px-6 py-4 flex items-center justify-between border-t border-border bg-card">
                  <div className="text-sm text-foreground">
                    Showing <span className="font-medium">{pagination.from}</span> to{' '}
                    <span className="font-medium">{pagination.to}</span> of{' '}
                    <span className="font-medium">{pagination.count}</span> features
                  </div>
                  <div className="flex gap-2">
                    {pagination.page > 1 && (
                      <Button
                        variant="outline"
                        onClick={() => handlePageChange(pagination.page - 1)}
                      >
                        Previous
                      </Button>
                    )}
                    {pagination.page < pagination.pages && (
                      <Button
                        variant="outline"
                        onClick={() => handlePageChange(pagination.page + 1)}
                      >
                        Next
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
          </div>
        </div>
      </div>
    </>
  )
}

Show.layout = (page: React.ReactNode) => <ProjectLayout>{page}</ProjectLayout>

export default Show
